<script lang="ts" setup>
import projectStore from '@/store/ProjectStore'
import ModalComponent from '@/components/ModalComponent.vue'
import { SchemeResponse } from '@/services/models/SchemeResponse'
import bus from '@/helper/Bus'
import { ModalTypes } from '@/helper/ModalTypes'
import { api } from '@/services/ApiService'
import { SchemeRequest } from '@/services/models/SchemeRequest'
import userStore from '@/store/UserStore'
import { BusEvent, HpStep, StringMap } from '@/helper/Types'
import FormElement from '@/components/FormElement.vue'
import { ValidatorType } from '@/helper/ValidateDirective'
import { computed, onMounted, onUnmounted, reactive, ref } from 'vue'
import { StepsController as stepsController } from '@/controllers/StepsController'
import SystemScheme from '@/components/steps/SystemScheme.vue'
import SectionHeadline from '@/components/SectionHeadline.vue'
import appStore from '@/store/AppStore.ts'
import ExclusionHint from '@/components/steps/components/ExclusionHint.vue'

const validator: ValidatorType = reactive({})

const requestSchemeMail = ref(userStore.email)
const requestSchemeSendCopy = ref(false)
const requestSchemeSuccess = ref(false)
const showRequestSchemeSuccess = ref()
const systemSchemes = ref<SchemeResponse[]>([])
const showSchemesBelowThreshold = ref(false)

const emit = defineEmits<{
  (e: 'validStateChange', change: boolean): void
}>()

bus.$onC(HpStep.SYSTEM_SCHEMES, BusEvent.REQUEST_SCHEME_CONFIRMED, requestSchemeConfirmed)

onMounted(async () => {
  systemSchemes.value = await projectStore.getSystemSchemes()
})

onUnmounted(() => {
  emit('validStateChange', true)
})

const schemesAboveThreshold = computed((): SchemeResponse[] => {
  return systemSchemes.value
    .filter(scheme => scheme.score.totalScorePercent >= userStore.minSystemSchemeScorePercent)
})

const schemesBelowThreshold = computed((): SchemeResponse[] => {
  return systemSchemes.value
    .filter(scheme => scheme.score.totalScorePercent < userStore.minSystemSchemeScorePercent)
})

function requestSchemeEmbeddedConfirmed(): void {
  if (!validator.email.validate()) {
    return
  }
  requestSchemeSuccess.value = true
  sendRequestScheme(requestSchemeMail.value, requestSchemeSendCopy.value)
}

async function explainSystemScheme() {
  const schemeFileName = prompt('File name of system scheme (e.g. 0020283746_01_SD_ES)')

  if (schemeFileName) {
    const errors = await api.explainSystemScheme(projectStore.projectId, schemeFileName)
    if (errors.length === 0) {
      alert('no errors found')
    } else {
      alert(errors.join('\n'))
    }
  }
}

async function requestSchemeClicked(e: Event) {
  if (((e as KeyboardEvent)?.ctrlKey || (e as KeyboardEvent)?.metaKey) && (e as KeyboardEvent)?.altKey && (e as KeyboardEvent)?.shiftKey) {
    await explainSystemScheme()
    return
  }

  bus.$emit(BusEvent.SHOW_MODAL, {
    modalType: ModalTypes.SCHEME,
    confirmEventName: BusEvent.REQUEST_SCHEME_CONFIRMED,
    payload: { mail: userStore.email }
  })
}

function requestSchemeConfirmed(payload: StringMap): void {
  bus.$emit(BusEvent.SHOW_MODAL, {
    modalType: ModalTypes.SCHEME_SUCCESS
  })

  sendRequestScheme(payload.mail, payload.sendCopy)
}

async function sendRequestScheme(mail: string, sendCopy: boolean): Promise<void> {
  const data = { email: mail, sendCopy: sendCopy } as SchemeRequest
  await api.requestScheme(projectStore.projectId, data)
}

function validate(): boolean {
  return true
}

defineExpose({
  validator,
  validate
})
</script>

<template>
  <div class="steps__content" data-hj-whitelist>
    <section>
      <div class="steps-table__header">
        <SectionHeadline class="steps-table__title fTable__title" translation-key="step_system_schemes.title_system_schemes" />

        <button
          v-if="systemSchemes.length && userStore.enableRequestSystemScheme"
          class="btn is--secondary is--right request-scheme-btn"
          @click="requestSchemeClicked"
        >
          {{ $t('step_system_schemes.btn_no_scheme_found') }}
        </button>
      </div>

      <form
        v-if="systemSchemes.length > 0 && !appStore.isLoading"
        id="system-schemes-form"
        action=""
        class="steps__form"
        @submit.prevent="stepsController.nextClicked()"
      >
        <div v-if="schemesAboveThreshold" class="system-schemes">
          <SystemScheme v-for="scheme in schemesAboveThreshold" :key="scheme.fileName" :scheme="scheme" />
        </div>

        <ExclusionHint
          v-if="schemesAboveThreshold.length === 0"
          :title="$t('step_system_schemes.title_no_feasable_system_schemes')"
          :text="$t('step_system_schemes.text_no_feasable_system_schemes')"
        />

        <button
          v-if="schemesBelowThreshold.length > 0 && !showSchemesBelowThreshold && schemesAboveThreshold.length !== 0"
          class="btn is--secondary show-more-schemes"
          @click.prevent="() => showSchemesBelowThreshold = true"
        >
          {{ $t('step_system_schemes.show_alternative_schemes') }}
        </button>

        <template v-if="showSchemesBelowThreshold || schemesAboveThreshold.length === 0">
          <SectionHeadline
            class="steps-table__title fTable__title"
            style="margin: 2rem 0;"
            translation-key="step_system_schemes.title_alternative_system_schemes"
          />

          <div class="system-schemes">
            <SystemScheme v-for="scheme in schemesBelowThreshold" :key="scheme.fileName" :scheme="scheme" />
          </div>
        </template>

        <input class="is--invisible" type="submit">
      </form>

      <div v-else>
        <div class="scheme__info" @click.ctrl.shift.alt="explainSystemScheme">
          <i class="ic-info-circle" />
          <p v-sanitize-html="$t('global.no_schemes_available_for_selection')" />
        </div>

        <div v-if="userStore.enableRequestSystemScheme">
          <ModalComponent
            v-if="!requestSchemeSuccess"
            :has-controls="true"
            :has-no-cancel="true"
            class="is--scheme is--embedded"
            @confirm="requestSchemeEmbeddedConfirmed"
          >
            <template #title>
              {{ $t('modal.scheme_title') }}
            </template>

            <template #text>
              <p v-sanitize-html="$t('modal.scheme_text')" />

              <FormElement
                v-validate="'required|email'"
                :error="validator.email?.error"
                :required="true"
                label-key="modal.scheme_input_label"
              >
                <input
                  id="email"
                  v-model="requestSchemeMail"
                  :placeholder="$t('step_details.placeholder_email')"
                  name="email"
                  type="email"
                >
              </FormElement>

              <label class="form__checkbox">
                <input id="cb-email-cop" v-model="requestSchemeSendCopy" type="checkbox">
                <span class="form__cm" />
                {{ $t('modal.scheme_checkbox_label') }}
              </label>
            </template>

            <template #confirm>
              {{ $t('modal.scheme_confirm_btn_text') }}
            </template>
          </ModalComponent>

          <ModalComponent
            v-if="requestSchemeSuccess"
            :has-controls="false"
            :has-no-cancel="true"
            class="is--scheme-success is--large is--embedded"
            @confirm="showRequestSchemeSuccess = false"
          >
            <template #title>
              {{ $t('modal.scheme_success_title') }}
            </template>

            <template #text>
              <div class="modal__icon">
                <i class="ic-check" />
              </div>
              <span>{{ $t('modal.scheme_success_text') }}</span>
            </template>

            <template #confirm>
              {{ $t('modal.scheme_success_confirm_btn_text') }}
            </template>
          </ModalComponent>
        </div>
      </div>
    </section>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';

.system-schemes {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;

  @include media('>tablet') {
    grid-template-columns: 1fr 1fr;
  }
}

.steps-table__header {
  margin-bottom: 2rem;
}

.scheme__info {
  text-align: center;
  color: $textColorLight;

  i[class^='ic-']:before,
  i[class*=' ic-']:before,
  i.ic:before {
    color: $textColorLight;
    font-size: rem-calc(70);
    margin-bottom: rem-calc(16);
  }

  p {
    margin-bottom: rem-calc(64);
  }
}

.show-more-schemes {
  margin-top: 2rem;
}
</style>
